import { LitElement, html, css } from 'lit';
import { StoreMixin } from '../store.js';
import { signInUrl } from '../api.js';
import { currentTool, onNavigate } from '../router.js';
import { brandLogo } from './brand-logo.js';

/**
 * The frame: app bar, run and freshness banner, shared filter bar, and the page. Also the two
 * states that are not a page — the shell still loading, and a visitor who is not signed in.
 *
 * The frame is tool-aware (Estimate Tool 2.3). The banner states which forecast run the numbers
 * came from and the filter bar carries the PRD 7.2 filters; neither means anything to an
 * estimate, so both render for Forecast only. In their place the Estimate tool shows one thin
 * strip: when the client ratings were last read, and how many clients cannot price yet.
 * Forecast's frame is what Phase 19 gated, unchanged.
 */
class AppRoot extends StoreMixin(LitElement) {
  static properties = { _tool: { state: true } };

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: var(--page);
    }
    main {
      max-width: var(--page-max);
      margin: 0 auto;
      padding: 22px var(--page-gutter) 48px;
    }
    .centre {
      min-height: 100vh;
      display: grid;
      place-items: center;
      padding: 24px;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 36px 40px;
      max-width: 460px;
      text-align: center;
    }
    .card svg {
      height: 34px;
      width: auto;
      margin: 0 auto 6px;
      color: var(--ink-strong);
    }
    .tag {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--ink-muted);
      margin-bottom: 18px;
    }
    h1 {
      font-size: 22px;
      margin: 0 0 8px;
      color: var(--ink-strong);
    }
    p {
      color: var(--ink-muted);
      margin: 0 0 20px;
    }
    a.button {
      display: inline-block;
      background: var(--ink-strong);
      color: var(--surface);
      text-decoration: none;
      font-weight: 600;
      padding: 10px 20px;
      border-radius: 6px;
    }
    a.button:hover {
      background: var(--eggplant-600);
    }
    .error {
      color: var(--status-overloaded);
      font-weight: 600;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._tool = currentTool();
    this._offNavigate = onNavigate(() => {
      this._tool = currentTool();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._offNavigate?.();
  }

  render() {
    const { ready, user, error } = this.appState;

    if (!ready) {
      return html`<div class="centre"><p>Loading…</p></div>`;
    }

    if (!user) {
      return html`
        <div class="centre">
          <div class="card">
            ${brandLogo(34)}
            <div class="tag">Resource Allocation Forecast</div>
            <h1>Sign in to continue</h1>
            <p>
              ${
                error
                  ? html`<span class="error">${error}</span>`
                  : 'Use your brandextract.com Google account.'
              }
            </p>
            <a class="button" href=${signInUrl()}>Sign in with Google</a>
          </div>
        </div>
      `;
    }

    const forecast = (this._tool ?? currentTool()) === 'forecast';
    return html`
      <app-nav></app-nav>
      ${
        forecast
          ? html`
              <run-banner></run-banner>
              <filter-bar></filter-bar>
            `
          : html`<estimate-strip></estimate-strip>`
      }
      <main><app-router></app-router></main>
      <chart-tooltip></chart-tooltip>
    `;
  }
}

customElements.define('app-root', AppRoot);
